import cuid from "cuid";
import type React from "react";
import { createContext, useCallback, useContext } from "react";

import type { AccountEventRequest, AccountEventType, PublishBroadcastResponse } from "@storyboard/web-client";
import {
  createAccountEvent,
  publishBroadcast as publishBroadcastRequest,
  uploadLogs as uploadLogsRequest,
} from "@storyboard/web-client";
import { transport } from "@/config";
import { createLogger } from "@/logger";
import { appContext, deviceContext } from "@/models/apiContext";

const log = createLogger("events");

import { upsertAccountEvent } from "@/models/repositories/AccountEvent";
import { UxContext } from "@/models/UxStateProvider";
import { recordEvent } from "@/services/telemetry";
import { idx, rowsByIndex, store } from "@/store/store";
import { enqueueCalculateUnreads } from "./actions/calculateUnreads";
import { queueSeenReceipt } from "./actions/receiptBatcher";
import { WorkspaceContext } from "./StateProviders/workspaceProvider";
import { TelemetryContext } from "./TelemetryProvider";

// Batch handler for hasRegisteredSeenEvent updates
const seenEventBatch: Set<string> = new Set();
let seenEventBatchTimer: NodeJS.Timeout | null = null;
const SEEN_EVENT_BATCH_DELAY = 100; // 100ms

function processBatchedSeenEvents() {
  if (seenEventBatch.size === 0) {
    seenEventBatchTimer = null;
    return;
  }

  const itemIds = Array.from(seenEventBatch);
  seenEventBatch.clear();
  seenEventBatchTimer = null;

  store.updateMany("item", itemIds, (row) =>
    row.hasRegisteredSeenEvent ? row : { ...row, hasRegisteredSeenEvent: true },
  );
}

function enqueueBatchedSeenEvent(itemId: string) {
  seenEventBatch.add(itemId);

  if (!seenEventBatchTimer) {
    seenEventBatchTimer = setTimeout(processBatchedSeenEvents, SEEN_EVENT_BATCH_DELAY);
  }
}

type ActionState = {
  accountEvent?: (event: AccountEventType, data: any) => void;
  publishBroadcast?: (params: {
    workspaceId: string;
    contentId: string;
    workspaceMembershipIds?: string[];
    feedIds?: string[];
    inputText?: string;
    isSilent?: boolean;
  }) => Promise<PublishBroadcastResponse>;
  sendFeedback?: (message: string) => Promise<void>;
};

export const ActionContext = createContext<ActionState>({});

type Props = {
  children: React.ReactNode | React.ReactNode[];
};

const ActionsProvider = ({ children }: Props) => {
  const { userReadOnlyMode } = useContext(UxContext);
  const { trackAction, finishAction } = useContext(TelemetryContext);
  const { myCurrentWorkspaceMembership } = useContext(WorkspaceContext);

  const accountEvent = useCallback(
    async (name: AccountEventType, data: any) => {
      if (userReadOnlyMode === true) {
        log.debug("Read only mode enabled, not sending event");
        return;
      }

      if (!myCurrentWorkspaceMembership) return;
      const myAcocuntId = myCurrentWorkspaceMembership.accountId;
      // A receipt on an item is sent once.
      if (data.feedId && data.feedItemId) {
        // Check if item already has the flag set to avoid unnecessary work
        const itemRecord = store.get("item", data.feedItemId);

        if (itemRecord?.hasRegisteredSeenEvent) {
          // Item already has the flag set, nothing to do
          return;
        }

        const existingEvent = rowsByIndex("accountEvent", idx.accountEventsByItem, data.feedItemId).find(
          (event) => event.name === name && event.feedId === data.feedId && event.accountId === myAcocuntId,
        );

        if (existingEvent) {
          log.debug("Already sent event", existingEvent);
          enqueueBatchedSeenEvent(data.feedItemId);
          return;
        }
      }
      const event: AccountEventRequest = {
        id: cuid(),
        name,
        ...data,
        timestamp: new Date(Date.now()).toISOString(),
        accountId: myAcocuntId,
      };
      // Seen receipts ride one batch request. The local `accountEvent` row is written now, so the item reads as seen at
      // once and the `existingEvent` guard above stops a re-render queueing it twice before the batch sends.
      if (name === "Saw Feed Item" && data.feedItemId) {
        queueSeenReceipt({ ...event, appContext, deviceContext });
        await upsertAccountEvent(event);
        enqueueBatchedSeenEvent(data.feedItemId);
      } else {
        void createAccountEvent(transport, { ...event, appContext, deviceContext });
        await upsertAccountEvent(event);
      }
      enqueueCalculateUnreads({
        feedIds: [event.feedId],
        membership: myCurrentWorkspaceMembership,
        alsoSetAsRead: true,
      });
    },
    [myCurrentWorkspaceMembership, userReadOnlyMode],
  );

  const publishBroadcast = useCallback(
    async ({
      workspaceId,
      contentId,
      workspaceMembershipIds,
      feedIds,
      inputText,
      isSilent,
    }: {
      workspaceId: string;
      contentId: string;
      workspaceMembershipIds?: string[];
      feedIds?: string[];
      inputText?: string;
      isSilent?: boolean;
    }) => {
      try {
        trackAction(
          { action: "publish_broadcast", target: contentId },
          {
            workspaceId,
            workspaceMembershipIds,
            feedIds,
            inputText,
            isSilent,
          },
        );
        const broadcast = await publishBroadcastRequest(transport, {
          workspaceId,
          contentId,
          workspaceMembershipIds,
          feedIds,
          inputText,
          isSilent,
        });
        finishAction({ action: "publish_broadcast", target: contentId });
        return broadcast;
      } catch (error) {
        finishAction({ action: "failed_publish_broadcast", target: contentId });
        // Recoverable: the failure is rethrown, the UI surfaces it, and the user retries.
        recordEvent("broadcast.publish_failed", {
          broadcast: {
            workspaceId,
            recipients: (feedIds?.length ?? 0) + (workspaceMembershipIds?.length ?? 0),
            error: error instanceof Error ? error.message : String(error),
          },
        });
        return Promise.reject(error);
      }
    },
    [trackAction, finishAction],
  );

  const sendFeedback = useCallback(async (message: string) => {
    const feedback = await uploadLogsRequest(transport, {
      logs: "",
      userMessage: message,
      appContext,
      deviceContext,
    });

    return feedback;
  }, []);

  const actionState: ActionState = {
    accountEvent,
    publishBroadcast,
    sendFeedback,
  };

  return <ActionContext.Provider value={actionState}>{children}</ActionContext.Provider>;
};

export default ActionsProvider;
